"use client";

import Contact from "@/components/contact";
import Testimonials from "@/components/testimonials";
import { Button } from "@/components/ui/button";
import { useUser } from "@/context/user-context";
import {
  caseStudies,
  our_customers,
  services,
  teamMembers,
} from "@/data/dummy";
import { ArrowRight } from "lucide-react";
import Link from "next/link";

export default function Home() {
  const { isAuthenticated } = useUser();
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section — exactly fills viewport minus fixed navbar */}
      <section className="relative min-h-[85vh] lg:min-h-[90vh] flex flex-col overflow-hidden">
        {/* Background Image & Overlay - improved depth */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: 'url("/images/hexad.jpg")' }}
        >
          <div className="absolute inset-0 bg-linear-to-br from-[#0F2929]/90 via-[#0F2929]/80 to-[#0A1F1F]/95" />
          <div className="absolute inset-0 bg-linear-to-t from-black/30 via-transparent to-transparent" />
        </div>

        {/* Animated accent line - optional decorative element */}
        <div className="absolute top-32 left-1/2 -translate-x-1/2 w-24 h-0.5 bg-[#0092B8]/40 rounded-full z-10" />

        {/* Main Content — vertically centered with better spacing */}
        <div className="relative z-10 flex-1 flex flex-col justify-center items-center text-center px-4 sm:px-6 lg:px-8 py-8 md:py-12 gap-5 md:gap-6">
          {/* Pill Badge — refined with subtle animation */}
          <div className="inline-flex items-center rounded-full bg-white/10 backdrop-blur-sm border border-white/20 px-5 py-px shadow-lg animate-fade-in-up">
            <span className="text-white text-[7px] font-semibold tracking-[0.25em] uppercase">
              Since 2021
            </span>
            <span className="mx-2 text-white/40">•</span>
            <span className="text-[#0092B8] text-[7px] font-semibold tracking-[0.25em] uppercase">
              Our Expertise
            </span>
          </div>

          {/* Headline — improved typography hierarchy */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-6xl font-bold text-white leading-[1.2] tracking-tight max-w-5xl animate-fade-in-up animation-delay-100">
            <span className="bg-linear-to-r from-white to-white/90 bg-clip-text text-transparent">
              Transforming Industries
            </span>
            <br className="hidden sm:block" />
            <span className="relative inline-block">
              Through Innovation
              <span className="absolute -bottom-2 left-0 right-0 h-[3px] bg-[#0092B8]/50 rounded-full hidden md:block" />
            </span>
            <br className="hidden sm:block" />
            <span className="text-white/95">& Excellence</span>
          </h1>

          {/* Subtitle — better readability and max-width */}
          <p className="max-w-2xl text-sm sm:text-base md:text-[1.125rem] text-white/85 leading-relaxed px-4 animate-fade-in-up animation-delay-200">
            Hexad partners with organizations across technology, engineering,
            construction, and business transformation to deliver practical
            solutions with measurable impact.
          </p>

          {/* Buttons — improved hover states and sizing */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-3 animate-fade-in-up animation-delay-300">
            <Link
              href={isAuthenticated ? "/request-quote" : "/auth/login"}
              className="w-full sm:w-auto group"
            >
              <button className="h-12 sm:h-12 px-7 sm:px-9 bg-[#0092B8] hover:bg-[#007a9a] text-white text-xs sm:text-sm font-bold uppercase tracking-wider rounded-full transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5 whitespace-nowrap flex items-center gap-2">
                Get Started
                <svg
                  className="w-4 h-4 transition-transform group-hover:translate-x-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 7l5 5m0 0l-5 5m5-5H6"
                  />
                </svg>
              </button>
            </Link>
            <Link href="/about" className="w-full sm:w-auto group">
              <button className="h-12 sm:h-12 px-7 sm:px-9 bg-transparent border-2 border-white/80 hover:border-white hover:bg-white/10 text-white hover:text-white text-xs sm:text-sm font-bold uppercase tracking-wider rounded-full transition-all duration-300 hover:-translate-y-0.5 whitespace-nowrap backdrop-blur-sm">
                Learn More
              </button>
            </Link>
          </div>
        </div>

        {/* Bottom Stats — refined with better visual separation */}
        <div className="relative z-10 w-full px-4 sm:px-6 lg:px-8 pb-10 md:pb-12">
          <div className="w-full max-w-5xl mx-auto">
            {/* Subtle linear line above stats */}
            <div className="flex justify-center mb-6">
              <div className="w-16 h-px bg-linear-to-r from-transparent via-white/30 to-transparent" />
            </div>
          </div>
        </div>
      </section>

      {/* Our Aproach Section */}
      <section className="relative lg:top-17 flex flex-col lg:flex-row items-center gap-6 lg:gap-8 h-auto lg:h-[550px] z-10 px-4 sm:px-6 md:max-w-[80%] mx-auto mt-10 lg:mt-0 mb-16 lg:mb-72">
        {/* Image Card */}

        <div className="group relative w-full lg:w-1/2 h-[260px] sm:h-80 lg:h-[670px]">
          <div className="w-full h-full overflow-hidden rounded-[30px] shadow-[0_30px_60px_rgba(0,0,0,0.12)]">
            <img
              src="https://i.pinimg.com/1200x/4d/de/02/4dde02825527c83bd74bc5d5d49ba5ac.jpg"
              alt="Hexad Consulting Solutions"
              className="w-full h-full object-cover object-right transition-transform duration-700 group-hover:scale-105"
            />
            {/* Subtle overlay */}
            <div className="absolute inset-0 bg-linear-to-tr from-black/10 to-transparent" />
          </div>

          {/* Floating secondary image card — hidden on small screens to avoid overflow */}
          <div className="hidden sm:block absolute bottom-[-30px] right-[-30px] p-3 bg-white rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.15)] transition-all duration-500 group-hover:-translate-y-2 group-hover:shadow-xl">
            <div className="w-[140px] h-[140px] lg:w-[180px] lg:h-[250px] overflow-hidden rounded-xl">
              <img
                src="https://i.pinimg.com/736x/35/67/f4/3567f4b4e231e832577f64ce8eeb996d.jpg"
                alt="Hexad Consulting Solutions"
                className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-110"
              />
            </div>
          </div>
        </div>

        {/* Text Content Card */}
        <div className="w-full lg:w-1/2 h-full bg-white p-6 sm:p-8 lg:p-12 flex flex-col justify-center border border-gray-100 relative">
          <div className="relative space-y-8">
            {/* Badge */}
            <div>
              <span className="inline-block px-3 py-1 bg-[#0092B8]/20 text-[#0092B8] text-xs uppercase font-semibold tracking-wide mb-4">
                OUR APPROACH
              </span>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 leading-tight">
                Innovative Solutions for Complex Challenges
              </h2>
            </div>

            {/* Description */}
            <p className="text-gray-600 leading-relaxed">
              At Hexad, we believe in partnership-driven consulting. We work
              closely with our clients to understand their unique challenges and
              deliver tailored solutions that create lasting impact and drive
              measurable results.
            </p>

            {/* Feature Grid - KEEPING THE SAME CHECKMARK GRID UI */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex items-center space-x-3">
                <div className="w-5 h-5 bg-[#0092B8] rounded-full flex items-center justify-center">
                  <svg
                    className="w-3 h-3 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={3}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <span className="text-gray-700 text-sm font-medium">
                  Quality Assured
                </span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-5 h-5 bg-[#0092B8] rounded-full flex items-center justify-center">
                  <svg
                    className="w-3 h-3 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={3}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <span className="text-gray-700 text-sm font-medium">
                  Expert Team
                </span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-5 h-5 bg-[#0092B8] rounded-full flex items-center justify-center">
                  <svg
                    className="w-3 h-3 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={3}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <span className="text-gray-700 text-sm font-medium">
                  Timely Delivery
                </span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-5 h-5 bg-[#0092B8] rounded-full flex items-center justify-center">
                  <svg
                    className="w-3 h-3 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={3}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <span className="text-gray-700 text-sm font-medium">
                  24/7 Support
                </span>
              </div>
            </div>

            {/* Clients Section - KEEPING SAME UI STRUCTURE */}
            <div className="pt-4">
              <div className="text-sm font-semibold text-gray-900">
                Trusted by Industry Leaders
              </div>
              <div className="text-xs text-gray-600">
                Join 100+ satisfied clients worldwide
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Link href={isAuthenticated ? "/request-quote" : "/auth/login"}>
                <Button
                  size="lg"
                  className="bg-[#0092B8] hover:bg-[#007a9a] text-white font-bold px-8 py-2 rounded-full transition-all duration-300 hover:-translate-y-0.5"
                >
                  Get Started Today
                </Button>
              </Link>
              <Link href="/bronchures">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-2 border-[#0F2929] text-[#0F2929] hover:bg-[#0F2929] hover:text-white font-bold px-8 py-2 rounded-full transition-all duration-300"
                >
                  Download Brochures
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="relative py-14 lg:py-16 px-4 sm:px-6 lg:px-8 md:max-w-[80%] mx-auto">
        <div className="max-w-6xl mx-auto">
          {/* Header - aligned with reference design styling */}
          <div className="text-center mb-16">
            <span className="inline-block px-3 py-1 bg-[#0092B8]/20 text-[#0092B8] text-xs uppercase font-semibold tracking-wide mb-4">
              Our Services
            </span>

            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 max-w-4xl mx-auto leading-tight">
              Comprehensive Solutions for Your Business
            </h2>

            <p className="text-gray-600 text-sm sm:text-base mt-4 max-w-2xl mx-auto leading-relaxed">
              We deliver innovative services tailored to drive your business
              growth and transformation
            </p>
          </div>

          {/* Services Grid - with enhanced card styling matching reference aesthetics */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {services.slice(0, 3).map((service, index) => (
              <div
                key={service.title}
                // href={`/services/${service.id}`}
                className="group relative bg-white rounded-2xl shadow-[0_20px_40px_rgba(0,0,0,0.08)] overflow-hidden hover:shadow-[0_30px_60px_rgba(0,0,0,0.15)] transition-all duration-500 hover:-translate-y-2 flex flex-col"
              >
                {/* Image Container - styled like reference image card */}
                <div className="relative overflow-hidden h-40 shrink-0">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  {/* Subtle gradient overlay - matching reference style */}
                  <div className="absolute inset-0 bg-linear-to-t from-black/20 via-transparent to-transparent" />

                  {/* Category Badge - updated to match reference badge styling */}
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-[#0092B8] text-white text-[10px] font-bold tracking-wide uppercase rounded-md">
                      {service.subTitle || service.title.split(" ")[0]}
                    </span>
                  </div>
                </div>

                {/* Content - with refined spacing and typography */}
                <div className="p-6 flex flex-col flex-1">
                  <h3 className="text-lg font-bold text-gray-900 mb-3 group-hover:text-[#0F2929] transition-colors duration-300 leading-snug line-clamp-1">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-6 flex-1 line-clamp-3">
                    {service.description}
                  </p>

                  {/* Learn More Link - styled like reference CTA but as link/button hybrid */}
                  <Link href={`/services/${service.id}`}>
                    <Button
                      variant="outline"
                      className="w-full border-2 border-white hover:text-[#0F2929] bg-[#0F2929] text-white font-semibold py-2 rounded-full transition-all duration-300 group/btn flex items-center justify-center gap-2"
                    >
                      Learn More
                      <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform duration-300" />
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>

          {/* Link to services */}
          <div className="text-center mt-8">
            <Link href="/services">
              <Button
                variant="outline"
                className="border-2 border-[#0F2929] text-[#0F2929] hover:bg-[#0F2929] hover:text-white font-bold px-6 py-2 rounded-full transition-all duration-300"
              >
                View All Services
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonies */}
      <Testimonials />

      {/* Team Section */}
      <section className="py-2 px-4 sm:px-6 lg:px-8 bg-white w-full max-w-[95%] sm:max-w-[90%] md:max-w-[80%] mx-auto">
        <div className="">
          {/* Header - Consistent with reference design */}
          <div className="text-center mb-16">
            <span className="inline-block px-3 py-1 bg-[#0092B8]/20 text-[#0092B8] text-xs uppercase font-semibold tracking-wide mb-4">
              OUR TEAM
            </span>

            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Meet The Experts
            </h2>

            <p className="text-gray-600 max-w-2xl mx-auto text-base leading-relaxed">
              Our diverse team of seasoned professionals brings decades of
              combined experience in technology, engineering, and business
              transformation.
            </p>
          </div>

          {/* Team member cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {teamMembers.slice(0, 3).map((member, index) => (
              <div
                key={index}
                className="group bg-white rounded-2xl border border-gray-100  transition-all duration-500 overflow-hidden"
              >
                {/* Image Container */}
                <div className="relative h-64 w-full overflow-hidden bg-linear-to-br from-gray-50 to-gray-100">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="h-full w-full object-cover object-top transition-transform duration-700"
                  />
                  {/* Subtle overlay on hover - matching reference style */}
                  <div className="absolute inset-0 bg-linear-to-t from-black/40 via-transparent to-transparent opacity-0 duration-500" />
                </div>

                {/* Content */}
                <div className="p-2">
                  <h3 className="text-xl font-bold text-[#0F2929] mb-1">
                    {member.name}
                  </h3>
                  <p className="text-[#0092B8] font-semibold text-sm mb-3">
                    {member.role}
                  </p>
                  <p className="text-gray-600 text-sm leading-relaxed mb-4 line-clamp-3">
                    {member.bio}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* CTA Button - Styled like reference buttons */}
          <div className="text-center mt-12">
            <Link href="/team">
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-[#0F2929] text-[#0F2929] hover:bg-[#0F2929] hover:text-white font-bold px-8 py-2 rounded-full transition-all duration-300 hover:-translate-y-0.5"
              >
                View All Team
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Contact CTA Section */}
      <Contact />
      {/* <div className="mb-7 w-full bg-white-400 h-px" /> */}

      {/* Case Studies Section - Stats Focused */}
      <section className="relative z-10 px-4 sm:px-6 md:max-w-[80%] mx-auto mb-16 lg:mb-72">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-5 lg:-mt-32 pt-10 pb-4">
            <p className="text-cyan-600 text-xs uppercase font-medium mb-3">
              Case Studies
            </p>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Proven Results
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              See how we've helped businesses achieve remarkable outcomes
              through innovative solutions
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Featured Case Study */}
            <div className="bg-white shadow-lg overflow-hidden group hover:shadow-xl transition-all duration-300 p-7">
              <div className="h-64 overflow-hidden relative">
                <img
                  src="https://i.pinimg.com/1200x/36/df/bd/36dfbd69ae843270294ae63ae75152b6.jpg"
                  alt="Featured Case Study"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-linear-to-t from-black/50 to-transparent"></div>
                <div className="absolute bottom-4 left-6 text-white">
                  <span className="bg-cyan-600 text-white px-3 py-1 text-xs font-medium mb-2 inline-block">
                    Featured
                  </span>
                  <h3 className="text-xl font-bold">
                    Nationwide Institutional Supply Programme
                  </h3>
                </div>
              </div>

              <div>
                <p className="text-gray-600 text-sm mb-4 my-5">
                  Delivering food supplies, construction materials, and
                  institutional goods to government facilities, correctional
                  services, schools, and NGOs across 10+ districts — from
                  Kigali to Gatsibo.
                </p>
                {/* <button className="w-full bg-cyan-600 hover:bg-cyan-700 text-white font-bold py-2 px-4 rounded-full transition-all duration-300 hover:-translate-y-0.5">
                  Read Full Case Study
                </button> */}
              </div>
            </div>

            {/* Smaller Case Studies */}
            <div className="space-y-6">
              {caseStudies.map((study, index) => (
                <div
                  key={index}
                  className="bg-white p-6 shadow-md hover:shadow-lg transition-all duration-300 group cursor-pointer"
                >
                  <div className="flex items-start justify-between mb-3">
                    <h4 className="font-bold text-gray-900 group-hover:text-cyan-600 transition-colors">
                      {study.title}
                    </h4>
                    <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs font-medium">
                      {study.category}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm text-gray-600">
                    <span className="font-semibold text-cyan-600">
                      {study.result}
                    </span>
                    <span>{study.duration}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
